import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import useAPI from '../../../api';
import { activityTypesAtom } from '../../../Atoms';
import formatAppointments from '../Utils/formatAppointments';

const isAbortError = (error) =>
  error?.name === 'AbortError' || error?.code === 20;

/**
 * Owns all appointment data for the Appointments page: range/branch driven
 * fetching (with abort on change to avoid races), local loading/error state,
 * and the create / update / end mutations. The page stays a thin orchestrator
 * and only wires UI + user feedback through the provided callbacks.
 *
 * `useAPI()` returns a fresh object every render, so it is kept in a ref and
 * deliberately excluded from dependency arrays to avoid re-running effects.
 *
 * @param {object}   params
 * @param {object}   params.selectedBranch  currently selected branch ({ id })
 * @param {Date[]}   params.range           [startDate, endDate] visible window
 * @param {boolean}  params.enabled         whether fetching is active
 */
const useAppointments = function useAppointments({
  selectedBranch,
  range,
  enabled = true,
}) {
  const API = useAPI();
  const apiRef = useRef(API);
  apiRef.current = API;

  const activityTypes = useAtomValue(activityTypesAtom);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tracks the in-flight fetch so range/branch changes can cancel it.
  const abortRef = useRef(null);

  const branchId = selectedBranch?.id;

  const fetchEvents = useCallback(() => {
    if (!enabled || !branchId || !range?.[0] || !range?.[1]) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const [startDate, endDate] = range;
    setLoading(true);
    setError(null);

    apiRef.current.getEventsByperiod(
      startDate.toISOString(),
      endDate.toISOString(),
      branchId,
      (data) => {
        if (controller.signal.aborted) return;
        setEvents(formatAppointments(data));
        setLoading(false);
      },
      (err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setError(err);
        setLoading(false);
      },
      // The API pages at 25 unless asked; a calendar view needs the whole range at once.
      // ponytail: fixed ceiling of 1000 events per view; page through if a view can exceed it.
      { silent: true, signal: controller.signal, pageNumber: 0, pageSize: 1000 }
    );
  }, [enabled, branchId, range]);

  useEffect(() => {
    fetchEvents();
    return () => abortRef.current?.abort();
  }, [fetchEvents]);

  const refetch = useCallback(() => fetchEvents(), [fetchEvents]);

  const createAppointment = useCallback(
    ({ entityId, scheduledOn, notes, assignee, remindBefore }, callbacks = {}) =>
      apiRef.current.activity(
        {
          entityId,
          branchId,
          activityTypeId: 4, // appointment
          notes,
          campaignId: null,
        },
        {
          scheduledOn,
          assignee: assignee === 'ALL' ? null : assignee,
          remindBefore: remindBefore || null,
        },
        {},
        (result) => {
          refetch();
          callbacks.onSuccess?.(result);
        },
        (err) => callbacks.onError?.(err)
      ),
    [branchId, refetch]
  );

  const updateAppointment = useCallback(
    (
      appointmentId,
      { entityId, scheduledOn, notes, assignee, remindBefore },
      callbacks = {}
    ) =>
      apiRef.current.updateAppointment(
        appointmentId,
        {
          entityId,
          assignee: assignee === 'ALL' ? null : assignee,
          notes,
          scheduledOn,
          remindBefore: remindBefore || null,
        },
        (result) => {
          refetch();
          callbacks.onSuccess?.(result);
        },
        (err) => callbacks.onError?.(err)
      ),
    [refetch]
  );

  const endAppointment = useCallback(
    (
      appointmentId,
      { entityId, outcome, notes, campaignId = null, nextActivityData },
      callbacks = {}
    ) =>
      apiRef.current.endAppointment(
        appointmentId,
        {
          entityId,
          branchId,
          campaignId: campaignId ?? null,
          outcome,
          notes,
          activityTypeId: activityTypes.find(
            (type) => type.name === 'APPOINTMENT'
          )?.id,
          nextActivityData,
        },
        (result) => {
          refetch();
          callbacks.onSuccess?.(result);
        },
        (err) => callbacks.onError?.(err)
      ),
    [branchId, activityTypes, refetch]
  );

  return {
    events,
    loading,
    error,
    refetch,
    createAppointment,
    updateAppointment,
    endAppointment,
  };
};

export default useAppointments;
